import { createContext, useContext, useEffect, useState } from "react";
import { Phone, MessageCircle, X, MapPin, Navigation } from "lucide-react";
import { useContent } from "./useContent";

const StorePickerContext = createContext(null);

// Haversine distance in km
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

export const StorePickerProvider = ({ children }) => {
  const [mode, setMode] = useState(null); // 'call' | 'whatsapp' | null
  const [userLoc, setUserLoc] = useState(null); // {lat, lng} | null
  const [locStatus, setLocStatus] = useState("idle"); // idle | loading | granted | denied | unavailable

  const open = (m) => setMode(m);
  const close = () => setMode(null);

  // Auto-request location once when picker first opens
  useEffect(() => {
    if (mode && !userLoc && locStatus === "idle" && navigator.geolocation) {
      setLocStatus("loading");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setLocStatus("granted");
        },
        () => setLocStatus("denied"),
        { timeout: 6000, maximumAge: 5 * 60 * 1000 }
      );
    } else if (mode && !navigator.geolocation && locStatus === "idle") {
      setLocStatus("unavailable");
    }
  }, [mode, userLoc, locStatus]);

  return (
    <StorePickerContext.Provider value={{ open, userLoc, locStatus }}>
      {children}
      <StorePickerModal mode={mode} onClose={close} userLoc={userLoc} locStatus={locStatus} />
    </StorePickerContext.Provider>
  );
};

export const useStorePicker = () => {
  const ctx = useContext(StorePickerContext);
  if (!ctx) throw new Error("useStorePicker must be used inside StorePickerProvider");
  return ctx;
};

// Helper: pick nearest open store from a stores array given a user location
export const useNearestStore = () => {
  const { getSection } = useContent();
  const { userLoc } = useContext(StorePickerContext) || { userLoc: null };
  const stores = (getSection("stores") || []).filter((s) => s.status === "open");
  if (!userLoc || !stores.length) return stores[0] || null;
  let best = null;
  let bestDist = Infinity;
  for (const s of stores) {
    if (typeof s.lat !== "number" || typeof s.lng !== "number") continue;
    const d = haversineKm(userLoc.lat, userLoc.lng, s.lat, s.lng);
    if (d < bestDist) {
      bestDist = d;
      best = s;
    }
  }
  return best || stores[0];
};

const StorePickerModal = ({ mode, onClose, userLoc, locStatus }) => {
  const { getSection } = useContent();
  if (!mode) return null;
  const stores = (getSection("stores") || []).filter((s) => s.status === "open");
  const isCall = mode === "call";
  const title = isCall
    ? "Which store would you like to call?"
    : "Which store would you like to chat with on WhatsApp?";

  // Compute nearest store
  let nearestIdx = -1;
  let nearestDistKm = null;
  if (userLoc) {
    let bestDist = Infinity;
    stores.forEach((s, i) => {
      if (typeof s.lat !== "number" || typeof s.lng !== "number") return;
      const d = haversineKm(userLoc.lat, userLoc.lng, s.lat, s.lng);
      if (d < bestDist) {
        bestDist = d;
        nearestIdx = i;
      }
    });
    if (nearestIdx >= 0) nearestDistKm = bestDist;
  }

  const handleAction = (store) => {
    if (isCall) {
      window.location.href = `tel:${(store.phone || "").replace(/\s/g, "")}`;
    } else if (store.whatsapp) {
      window.open(store.whatsapp, "_blank", "noopener,noreferrer");
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-[fadeInUp_0.2s_ease-out]"
      onClick={onClose}
      data-testid="store-picker-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md sm:rounded-3xl rounded-t-3xl bg-white p-6 shadow-2xl border-t sm:border border-sky-100"
        data-testid="store-picker-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors"
          aria-label="Close"
          data-testid="store-picker-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="mb-5">
          <div
            className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-3 ${
              isCall ? "bg-sky-100 text-sky-600" : "bg-green-100 text-green-600"
            }`}
          >
            {isCall ? <Phone className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
          </div>
          <h3 className="text-lg font-bold text-slate-900 leading-snug pr-8">{title}</h3>
          <p className="text-sm text-slate-500 mt-1">
            {locStatus === "loading"
              ? "Finding your nearest store…"
              : nearestIdx >= 0
              ? "We've highlighted the store closest to you."
              : "Pick the store nearest to you."}
          </p>
        </div>

        <div className="space-y-2.5">
          {stores.map((store, i) => {
            const isNearest = i === nearestIdx;
            return (
              <button
                key={i}
                onClick={() => handleAction(store)}
                className={`relative w-full flex items-center gap-3 p-4 rounded-2xl border transition-colors text-left group ${
                  isNearest
                    ? "border-sky-400 bg-sky-50 ring-2 ring-sky-100"
                    : "border-slate-200 hover:border-sky-300 hover:bg-sky-50/40"
                }`}
                data-testid={`store-picker-${mode}-${i}`}
              >
                {isNearest && (
                  <span
                    className="absolute -top-2 left-3 inline-flex items-center gap-1 bg-sky-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow"
                    data-testid={`store-picker-nearest-badge-${i}`}
                  >
                    <Navigation className="w-2.5 h-2.5" />
                    Nearest{nearestDistKm !== null ? ` · ${nearestDistKm.toFixed(1)} km` : ""}
                  </span>
                )}
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isNearest ? "bg-white" : "bg-sky-50"
                  }`}
                >
                  <MapPin className="w-4 h-4 text-sky-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm">{store.name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {isCall
                      ? store.phone
                      : store.whatsapp
                      ? store.whatsapp.replace("https://", "")
                      : store.phone}
                  </p>
                </div>
                <div
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    isCall
                      ? "bg-sky-600 text-white group-hover:bg-sky-700"
                      : "bg-green-500 text-white group-hover:bg-green-600"
                  }`}
                >
                  {isCall ? "Call" : "Chat"}
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-400 text-center mt-5">
          {locStatus === "denied"
            ? "Allow location to see your nearest store automatically."
            : "Both stores deliver across Jaipur — pick whichever is closer."}
        </p>
      </div>
    </div>
  );
};
